import { supabase } from "~/integrations/supabase/client";
import type {
  FlatLocation,
  Hallway,
  HallwayFormData,
  LocationFilters,
  Position,
  PositionFormData,
  Rack,
  RackFormData,
  Warehouse,
  WarehouseFormData,
} from "~/types/wms";

export const warehouseService = {
  // ============================================================================
  // Warehouses
  // ============================================================================

  async getWarehouses(): Promise<Warehouse[]> {
    const { data, error } = await supabase
      .from("wms_warehouses")
      .select("*")
      .order("code", { ascending: true });

    if (error) throw error;
    return data as Warehouse[];
  },

  async getWarehouseById(id: string): Promise<Warehouse> {
    const { data, error } = await supabase
      .from("wms_warehouses")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as Warehouse;
  },

  async createWarehouse(formData: WarehouseFormData): Promise<Warehouse> {
    const { data, error } = await supabase
      .from("wms_warehouses")
      .insert(formData)
      .select()
      .single();

    if (error) throw error;
    return data as Warehouse;
  },

  async updateWarehouse(
    id: string,
    formData: Partial<WarehouseFormData>
  ): Promise<Warehouse> {
    const { data, error } = await supabase
      .from("wms_warehouses")
      .update({ ...formData, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as Warehouse;
  },

  async deleteWarehouse(id: string): Promise<void> {
    const { error } = await supabase
      .from("wms_warehouses")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },

  // ============================================================================
  // Hallways
  // ============================================================================

  async getHallways(warehouseId?: string): Promise<Hallway[]> {
    let query = supabase
      .from("wms_hallways")
      .select("*, warehouse:wms_warehouses(*)")
      .order("sequence", { ascending: true });

    if (warehouseId) {
      query = query.eq("warehouse_id", warehouseId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as Hallway[];
  },

  async getHallwayById(id: string): Promise<Hallway> {
    const { data, error } = await supabase
      .from("wms_hallways")
      .select("*, warehouse:wms_warehouses(*)")
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as Hallway;
  },

  async createHallway(formData: HallwayFormData): Promise<Hallway> {
    // full_code = hallway code only (no warehouse prefix) e.g. "B1"
    const full_code = formData.code;

    const { data, error } = await supabase
      .from("wms_hallways")
      .insert({ ...formData, full_code })
      .select()
      .single();

    if (error) throw error;
    return data as Hallway;
  },

  async updateHallway(
    id: string,
    formData: Partial<HallwayFormData>
  ): Promise<Hallway> {
    const updateData: Record<string, unknown> = {
      ...formData,
      updated_at: new Date().toISOString(),
    };

    // If code is being updated, we need to update full_code
    if (formData.code) {
      updateData.full_code = formData.code;
    }

    const { data, error } = await supabase
      .from("wms_hallways")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as Hallway;
  },

  async deleteHallway(id: string): Promise<void> {
    const { error } = await supabase.from("wms_hallways").delete().eq("id", id);

    if (error) throw error;
  },

  // ============================================================================
  // Racks
  // ============================================================================

  async getRacks(hallwayId?: string): Promise<Rack[]> {
    let query = supabase
      .from("wms_racks")
      .select("*, hallway:wms_hallways(*, warehouse:wms_warehouses(*))")
      .order("sequence", { ascending: true });

    if (hallwayId) {
      query = query.eq("hallway_id", hallwayId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as Rack[];
  },

  async getRackById(id: string): Promise<Rack> {
    const { data, error } = await supabase
      .from("wms_racks")
      .select("*, hallway:wms_hallways(*, warehouse:wms_warehouses(*))")
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as Rack;
  },

  async createRack(formData: RackFormData): Promise<Rack> {
    // Get hallway to build full_code
    const hallway = await this.getHallwayById(formData.hallway_id);
    const full_code = `${hallway.full_code}-${formData.code}`;
    const barcode = formData.barcode || full_code;

    const { data, error } = await supabase
      .from("wms_racks")
      .insert({ ...formData, full_code, barcode })
      .select()
      .single();

    if (error) throw error;
    return data as Rack;
  },

  async updateRack(id: string, formData: Partial<RackFormData>): Promise<Rack> {
    const updateData: Record<string, unknown> = {
      ...formData,
      updated_at: new Date().toISOString(),
    };

    // If code is being updated, we need to update full_code
    if (formData.code) {
      const rack = await this.getRackById(id);
      const hallway = await this.getHallwayById(rack.hallway_id);
      updateData.full_code = `${hallway.full_code}-${formData.code}`;
      if (!formData.barcode) {
        updateData.barcode = updateData.full_code;
      }
    }

    const { data, error } = await supabase
      .from("wms_racks")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as Rack;
  },

  async deleteRack(id: string): Promise<void> {
    const { error } = await supabase.from("wms_racks").delete().eq("id", id);

    if (error) throw error;
  },

  // ============================================================================
  // Bulk Rack Creation
  // ============================================================================

  async createRacksForHallway(
    hallwayId: string,
    count: number
  ): Promise<Rack[]> {
    const hallway = await this.getHallwayById(hallwayId);
    const racks = [];

    for (let i = 1; i <= count; i++) {
      const code = String(i).padStart(2, "0");
      const full_code = `${hallway.full_code}-${code}`;
      racks.push({
        hallway_id: hallwayId,
        code,
        full_code,
        barcode: full_code,
        rack_type: "storage" as const,
        sequence: i,
        is_active: true,
      });
    }

    const { data, error } = await supabase
      .from("wms_racks")
      .insert(racks)
      .select();

    if (error) throw error;
    return data as Rack[];
  },

  // ============================================================================
  // Positions
  // ============================================================================

  async getPositions(rackId?: string): Promise<Position[]> {
    let query = supabase
      .from("wms_positions")
      .select(
        "*, rack:wms_racks(*, hallway:wms_hallways(*, warehouse:wms_warehouses(*)))"
      )
      .order("level", { ascending: true })
      .order("code", { ascending: true });

    if (rackId) {
      query = query.eq("rack_id", rackId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as Position[];
  },

  async getPositionById(id: string): Promise<Position> {
    const { data, error } = await supabase
      .from("wms_positions")
      .select(
        "*, rack:wms_racks(*, hallway:wms_hallways(*, warehouse:wms_warehouses(*)))"
      )
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as Position;
  },

  async createPosition(formData: PositionFormData): Promise<Position> {
    // Get rack to build full_code
    const rack = await this.getRackById(formData.rack_id);
    const full_code = `${rack.full_code}-${formData.code}`;
    const barcode = formData.barcode || full_code;

    const { data, error } = await supabase
      .from("wms_positions")
      .insert({ ...formData, full_code, barcode })
      .select()
      .single();

    if (error) throw error;
    return data as Position;
  },

  async updatePosition(
    id: string,
    formData: Partial<PositionFormData>
  ): Promise<Position> {
    const updateData: Record<string, unknown> = {
      ...formData,
      updated_at: new Date().toISOString(),
    };

    // If code is being updated, we need to update full_code
    if (formData.code) {
      const position = await this.getPositionById(id);
      const rack = await this.getRackById(position.rack_id);
      updateData.full_code = `${rack.full_code}-${formData.code}`;
      if (!formData.barcode) {
        updateData.barcode = updateData.full_code;
      }
    }

    const { data, error } = await supabase
      .from("wms_positions")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as Position;
  },

  async deletePosition(id: string): Promise<void> {
    const { error } = await supabase
      .from("wms_positions")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },

  // ============================================================================
  // Bulk Position Creation
  // ============================================================================

  async createPositionsForRack(
    rackId: string,
    levels: number
  ): Promise<Position[]> {
    const rack = await this.getRackById(rackId);
    const LEVEL_LETTERS = "ABCDEFGHIJ";
    const positions: PositionFormData[] = [];

    for (let level = 1; level <= levels; level++) {
      const code = LEVEL_LETTERS[level - 1];

      // Calculate ergonomic score based on level (golden zone = levels B-C / 2-3)
      let ergonomic_score = 25;
      if (level === 2 || level === 3) {
        ergonomic_score = 50; // Golden zone
      } else if (level === 4) {
        ergonomic_score = 35;
      } else if (level >= 5) {
        ergonomic_score = 15;
      }

      positions.push({
        rack_id: rackId,
        code,
        level,
        ergonomic_score,
      });
    }

    const { data, error } = await supabase
      .from("wms_positions")
      .insert(
        positions.map((p) => ({
          ...p,
          full_code: `${rack.full_code}-${p.code}`,
          barcode: `${rack.full_code}-${p.code}`,
        }))
      )
      .select();

    if (error) throw error;
    return data as Position[];
  },

  // ============================================================================
  // Location Tree (Full Hierarchy)
  // ============================================================================

  async getLocationTree(): Promise<
    (Warehouse & {
      hallways: (Hallway & { racks: (Rack & { positions: Position[] })[] })[];
    })[]
  > {
    const { data, error } = await supabase
      .from("wms_warehouses")
      .select(
        `
        *,
        hallways:wms_hallways(
          *,
          racks:wms_racks(
            *,
            positions:wms_positions(*)
          )
        )
      `
      )
      .order("code", { ascending: true });

    if (error) throw error;
    return data as (Warehouse & {
      hallways: (Hallway & { racks: (Rack & { positions: Position[] })[] })[];
    })[];
  },

  // ============================================================================
  // Flat Location List (All Locations)
  // ============================================================================

  async getAllLocations(filters?: LocationFilters): Promise<FlatLocation[]> {
    const results: FlatLocation[] = [];

    // Fetch all hallways with warehouse info
    if (!filters?.type || filters.type === "hallway") {
      let hallwayQuery = supabase
        .from("wms_hallways")
        .select("*, warehouse:wms_warehouses(id, code, name)")
        .order("full_code", { ascending: true });

      if (filters?.warehouse_id) {
        hallwayQuery = hallwayQuery.eq("warehouse_id", filters.warehouse_id);
      }
      if (filters?.is_active !== undefined) {
        hallwayQuery = hallwayQuery.eq("is_active", filters.is_active);
      }

      const { data: hallways, error: hallwayError } = await hallwayQuery;
      if (hallwayError) throw hallwayError;

      for (const h of hallways || []) {
        results.push({
          id: h.id,
          type: "hallway",
          code: h.code,
          full_code: h.full_code,
          warehouse_id: h.warehouse_id,
          warehouse_code: h.warehouse?.code || "",
          warehouse_name: h.warehouse?.name || "",
          is_active: h.is_active,
        });
      }
    }

    // Fetch all racks with hallway and warehouse info
    if (!filters?.type || filters.type === "rack") {
      let rackQuery = supabase
        .from("wms_racks")
        .select(
          "*, hallway:wms_hallways(id, code, warehouse_id, warehouse:wms_warehouses(id, code, name))"
        )
        .order("full_code", { ascending: true });

      if (filters?.hallway_id) {
        rackQuery = rackQuery.eq("hallway_id", filters.hallway_id);
      }
      if (filters?.is_active !== undefined) {
        rackQuery = rackQuery.eq("is_active", filters.is_active);
      }

      const { data: racks, error: rackError } = await rackQuery;
      if (rackError) throw rackError;

      for (const r of racks || []) {
        // Filter by warehouse_id if specified (need to check via hallway)
        if (
          filters?.warehouse_id &&
          r.hallway?.warehouse_id !== filters.warehouse_id
        ) {
          continue;
        }

        results.push({
          id: r.id,
          type: "rack",
          code: r.code,
          full_code: r.full_code,
          warehouse_id: r.hallway?.warehouse_id || "",
          warehouse_code: r.hallway?.warehouse?.code || "",
          warehouse_name: r.hallway?.warehouse?.name || "",
          hallway_id: r.hallway_id,
          hallway_code: r.hallway?.code || "",
          is_active: r.is_active,
          rack_type: r.rack_type,
          barcode: r.barcode,
        });
      }
    }

    // Fetch all positions with rack, hallway and warehouse info
    if (!filters?.type || filters.type === "position") {
      let positionQuery = supabase
        .from("wms_positions")
        .select(
          "*, rack:wms_racks(id, code, hallway_id, hallway:wms_hallways(id, code, warehouse_id, warehouse:wms_warehouses(id, code, name)))"
        )
        .order("full_code", { ascending: true });

      if (filters?.rack_id) {
        positionQuery = positionQuery.eq("rack_id", filters.rack_id);
      }
      if (filters?.is_active !== undefined) {
        positionQuery = positionQuery.eq("is_active", filters.is_active);
      }

      const { data: positions, error: positionError } = await positionQuery;
      if (positionError) throw positionError;

      for (const p of positions || []) {
        // Filter by warehouse_id if specified
        if (
          filters?.warehouse_id &&
          p.rack?.hallway?.warehouse_id !== filters.warehouse_id
        ) {
          continue;
        }
        // Filter by hallway_id if specified
        if (filters?.hallway_id && p.rack?.hallway_id !== filters.hallway_id) {
          continue;
        }

        results.push({
          id: p.id,
          type: "position",
          code: p.code,
          full_code: p.full_code,
          warehouse_id: p.rack?.hallway?.warehouse_id || "",
          warehouse_code: p.rack?.hallway?.warehouse?.code || "",
          warehouse_name: p.rack?.hallway?.warehouse?.name || "",
          hallway_id: p.rack?.hallway_id || "",
          hallway_code: p.rack?.hallway?.code || "",
          rack_id: p.rack_id,
          rack_code: p.rack?.code || "",
          level: p.level,
          is_active: p.is_active,
          barcode: p.barcode,
        });
      }
    }

    // Sort all results by full_code
    results.sort((a, b) => a.full_code.localeCompare(b.full_code));

    return results;
  },
};
